<template>
  <el-dialog
    :title="$t('university_request_title')"
    :visible.sync="dialogVisible"
    width="60%"
    custom-class="university-request-dialog"
    :append-to-body="true"
    :close-on-click-modal="false"
  >
    <!-- Contact details reminder, with a link to edit them without closing this dialog -->
    <el-alert
      :type="hasContactDetails ? 'info' : 'warning'"
      :closable="false"
      show-icon
    >
      <span>
        {{
          hasContactDetails
            ? $t('university_request_contact_info')
            : $t('university_request_contact_missing')
        }}
      </span>
      <el-button type="text" @click="$emit('edit-contact')">
        {{ $t('edit_contact_details') }}
      </el-button>
    </el-alert>

    <el-form
      ref="universityRequestForm"
      :model="form"
      label-position="top"
      size="small"
      v-loading="sending"
    >
      <el-form-item :label="$t('university_request_name')" required>
        <el-input
          v-model="form.universityName"
          :placeholder="$t('fill_in_uni')"
          maxlength="255"
          @input="universityNameChanged"
        ></el-input>
      </el-form-item>
      <el-form-item :label="$t('university_request_message')" required>
        <el-input
          type="textarea"
          v-model="form.message"
          :autosize="{ minRows: 6, maxRows: 12 }"
          @input="messageEdited = true"
        ></el-input>
      </el-form-item>
      <el-form-item :label="$t('university_request_file')" required>
        <el-upload
          ref="upload"
          action=""
          accept=".pdf"
          :auto-upload="false"
          :file-list="fileList"
          :on-change="fileChanged"
          :on-remove="fileRemoved"
        >
          <el-button slot="trigger" size="small" type="primary">
            {{ $t('select_file') }}
          </el-button>
          <div class="el-upload__tip" slot="tip">
            {{ $t('university_request_file_hint') }}
          </div>
        </el-upload>
      </el-form-item>
    </el-form>

    <span slot="footer">
      <el-button size="small" @click="dialogVisible = false">
        {{ $t('university_request_cancel') }}
      </el-button>
      <el-button
        size="small"
        type="primary"
        :disabled="!canSend"
        :loading="sending"
        @click="sendRequest"
      >
        {{ $t('send') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import axios from 'axios'
import config from '../../config.js'
import sharedMethods from './sharedMethods.js'

// Same size limit as the application attachments (AttachedDocuments.vue)
const MAX_FILE_SIZE = 13000000

export default {
  name: 'UniversityRequestDialog',
  mixins: [sharedMethods],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // Country of the university, as shown in the message template
    countryName: {
      type: String,
      default: '',
    },
    personalDetails: {
      type: Object,
      default: () => ({}),
    },
    hasContactDetails: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sending: false,
      // True once the user types in the message, so the template stops overwriting it
      messageEdited: false,
      fileList: [],
      form: {
        universityName: '',
        message: '',
        file: null,
      },
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
    canSend() {
      return (
        this.hasContactDetails &&
        this.form.universityName.trim() !== '' &&
        this.form.message.trim() !== '' &&
        this.form.file !== null &&
        !this.sending
      )
    },
  },
  watch: {
    visible(newVal) {
      // Refresh the template with the current country and name, unless the user edited it
      if (newVal && !this.messageEdited) {
        this.fillMessageTemplate()
      }
    },
  },
  methods: {
    fillMessageTemplate() {
      const pd = this.personalDetails || {}
      this.form.message = this.$t('university_request_template', {
        university: this.form.universityName.trim() || '...',
        country: this.countryName || '...',
        name: pd.name || '',
        surname: pd.surname || '',
      })
    },
    universityNameChanged() {
      this.form.universityName = this.form.universityName.toUpperCase()
      if (!this.messageEdited) {
        this.fillMessageTemplate()
      }
    },
    fileChanged(file) {
      const isPdf =
        file.raw.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf')
      if (!isPdf || file.size >= MAX_FILE_SIZE) {
        this.$notify.warning({
          title: this.$t('warning'),
          message: !isPdf
            ? this.$t('university_request_pdf_only')
            : this.$t('university_request_file_too_large'),
          offset: 100,
        })
        // Keep the previously selected file, if any
        this.fileList = this.form.file ? [...this.fileList] : []
        return
      }
      // Only one file is allowed: the latest selection replaces the previous one
      this.fileList = [file]
      this.form.file = file.raw
    },
    fileRemoved() {
      this.fileList = []
      this.form.file = null
    },
    resetForm() {
      this.messageEdited = false
      this.fileList = []
      this.form = {
        universityName: '',
        message: '',
        file: null,
      }
    },
    sendRequest() {
      if (!this.canSend) {
        return
      }
      const userId = JSON.parse(sessionStorage.getItem('externalUserId'))
      // Sent as multipart/form-data, so the backend receives the file with its original name
      const formData = new FormData()
      formData.append('message', this.form.message.trim())
      formData.append('file', this.form.file, this.form.file.name)
      this.sending = true
      axios
        .post(
          `${config.BASE_URL}/externalusers/${userId}/universityRequest`,
          formData
        )
        .then(response => {
          this.sending = false
          this.backendSuccessMsg(response)
          this.$emit('sent')
          this.resetForm()
          this.dialogVisible = false
        })
        .catch(err => {
          this.sending = false
          this.backendErrorNotify(err)
        })
    },
  },
}
</script>

<style lang="scss">
@import '../../styles/variables.scss';

// Not scoped: the dialog is appended to body
.university-request-dialog {
  .el-alert {
    margin-bottom: 15px;
    .el-alert__description,
    .el-alert__title {
      word-break: normal;
    }
    .el-button--text {
      padding: 0 0 0 5px;
    }
  }
  .el-dialog__body {
    padding-top: 10px;
  }
}

@media only screen and (max-width: $screen-sm-min) {
  .university-request-dialog {
    width: 90% !important;
  }
}
</style>
